import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {Button, ListItemText, Menu, MenuItem, Paper, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {AutomationType, type AutomationFlow} from "crqlar-react-common/dist/models/automation/automation";
import type {AutomationTemplateDefinition} from "crqlar-react-common/dist/models/automation/automationTemplate.model";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {FLOATING_SHADOW} from "../../../common/newVariablePicker/shared/floatingShadow";

export const BLANK_TEMPLATE_KEY = 'BLANK';

/** The empty canvas the dialog rests on, and what "Remove template" goes back to. */
export const blankAutomationDraft = (): AutomationFlow => ({
    type: AutomationType.ADVANCED,
    nodes: [],
    connections: [],
});

type Props = {
    templates: AutomationTemplateDefinition[]
    automation: AutomationFlow
    onChange: (automation: AutomationFlow) => void
    selectedKey: string
    onSelectedKeyChange: (key: string) => void
    nameIsSuggestion: boolean
    onNameIsSuggestionChange: (suggested: boolean) => void
    disabled?: boolean
};

function AutomationTemplatePicker({
    templates,
    automation,
    onChange,
    selectedKey,
    onSelectedKeyChange,
    nameIsSuggestion,
    onNameIsSuggestionChange,
    disabled,
}: Props) {
    const {t} = useTranslation();

    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const menuOpen = !!menuAnchor;

    const selected = templates.find(entry => entry.key === selectedKey);

    const apply = (key: string) => {
        const definition = templates.find(entry => entry.key === key);

        // Drawn as the API sent them; on confirm the flow is created from the template key alone.
        const base: AutomationFlow = definition
            ? {type: AutomationType.ADVANCED, nodes: definition.nodes, connections: definition.connections}
            : blankAutomationDraft();

        // Preserve author-written names when switching templates.
        const keepTyped = !!automation.name?.trim() && !nameIsSuggestion;
        // The API hands the title over already translated; only the blank option's name is ours to say.
        const suggestion = definition ? definition.title : t('New empty automation');
        const name = keepTyped ? automation.name : suggestion;

        onSelectedKeyChange(key);
        onNameIsSuggestionChange(!keepTyped);
        onChange({...base, name, description: automation.description});
    };

    return (
        <Stack gap={1.5}>
            <Typography variant={'subtitle2'} color={'text.secondary'}>
                {t('Start with')}
            </Typography>

            <Paper variant={'outlined'} sx={{p: 2}}>
                <Stack gap={0.25}>
                    <Typography variant={'body1'} sx={{fontWeight: 500}}>
                        {selected ? selected.title : t('New empty automation')}
                    </Typography>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {selected ? selected.description : t('Start from an empty canvas')}
                    </Typography>
                </Stack>

                <Stack direction={'row'} flexWrap={'wrap'} gap={0.5} sx={{mt: 1.5}}>
                    <Button
                        id={'automation_template_button'}
                        variant={'text'}
                        color={'inherit'}
                        endIcon={<ExpandMoreIcon/>}
                        disabled={disabled}
                        aria-haspopup={'menu'}
                        aria-controls={menuOpen ? 'automation_template_menu' : undefined}
                        aria-expanded={menuOpen ? 'true' : undefined}
                        onClick={event => setMenuAnchor(event.currentTarget)}
                        sx={{color: 'text.secondary'}}
                    >
                        {t('Use a template')}
                    </Button>

                    {!!selected && (
                        <Button
                            variant={'text'}
                            color={'inherit'}
                            startIcon={<CloseIcon/>}
                            disabled={disabled}
                            onClick={() => apply(BLANK_TEMPLATE_KEY)}
                            sx={{color: 'text.secondary'}}
                        >
                            {t('Remove template')}
                        </Button>
                    )}
                </Stack>

                <Menu
                    id={'automation_template_menu'}
                    anchorEl={menuAnchor}
                    open={menuOpen}
                    onClose={() => setMenuAnchor(null)}
                    MenuListProps={{'aria-labelledby': 'automation_template_button'}}
                    PaperProps={{variant: 'outlined', elevation: 0, sx: {minWidth: 300, maxWidth: 440, boxShadow: FLOATING_SHADOW}}}
                >
                    {templates.length === 0 && (
                        <MenuItem disabled>{t('No templates available')}</MenuItem>
                    )}
                    {templates.map(definition => (
                        <MenuItem
                            key={definition.key}
                            selected={definition.key === selectedKey}
                            sx={{whiteSpace: 'normal'}}
                            onClick={() => {
                                setMenuAnchor(null);
                                apply(definition.key);
                            }}
                        >
                            <ListItemText
                                primary={definition.title}
                                secondary={definition.description}
                            />
                        </MenuItem>
                    ))}
                </Menu>
            </Paper>
        </Stack>
    );
}

export default AutomationTemplatePicker;
